import { NgInject } from '@df/ng/ng-inject';
import { App } from '@df/core/app';
import compact from 'lodash-es/compact';
import findIndex from 'lodash-es/findIndex';
import { Model } from '@df/catalog/model';
import { ListingFilter } from '@df/catalog-ui/listing/listing';
import { Filter, IFilterConfig } from '@df/catalog-ui/listing/filter/filter';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';

export interface IMatchesCounts {
    [key: string]: number;
}

@ListingFilter({
    filterType: 'property'
})
export class FilterProperty<
    TModel extends Model,
    TProperty extends string | number = string | number,
    TOption = TProperty,
    TOptionWithCount = { option: TOption; count: number },
    TConfig extends IFilterConfig<string> = IFilterConfig<string>
> extends Filter<TModel, TProperty[], string, TConfig> {
    /**
     * Initial selection
     */
    protected override _selection?: TProperty[] = [];

    @NgInject(NCatalogUi.OPTIONS)
    private moduleConfig!: NCatalogUi.IModuleOptions;

    /**
     * If we use attribute grouping
     */
    protected get useGroups() {
        return this.moduleConfig.listing.filters.useGroups;
    }

    /**
     * Buffer for filtered models
     */
    protected _filteredModels: TModel[] | undefined;

    /**
     * Buffer for options with matches
     */
    protected _optionsWithMatches: TOptionWithCount[] | undefined;

    /**
     * Buffer for options with current matches
     */
    protected _optionsWithCurrentMatches: TOptionWithCount[] | undefined;

    /**
     * Buffer for any matches check
     */
    protected _anyMatches: boolean | undefined;

    /**
     * Buffere for any current matches check
     */
    protected _anyCurrentMatches: boolean | undefined;

    /**
     * Buffer for many matches check
     */
    protected _manyMatches: boolean | undefined;

    /**
     * Buffer for many current matches check
     */
    protected _manyCurrentMatches: boolean | undefined;

    /**
     * Buffer for matches count
     */
    protected _matchesCounts?: IMatchesCounts;

    /**
     * Buffer for current matches count
     */
    protected _matchesCountsCurrent?: IMatchesCounts;

    /**
     * Attribute options
     */
    protected _optionsIndexed?: {
        [key: string]: TOption;
    };

    /**
     * Initial hook
     * subscribes to listing changes to reset cached data
     */
    protected override onInit() {
        // if models change reset all meta data (we dont need to update this data, it will be auto set when needed)
        this.listing.subscribe(() => {
            this._filteredModels = undefined;

            this._optionsWithMatches = undefined;
            this._optionsWithCurrentMatches = undefined;

            this._anyMatches = undefined;
            this._anyCurrentMatches = undefined;
            this._manyCurrentMatches = undefined;

            this._manyMatches = undefined;

            this._matchesCounts = undefined;
            this._matchesCountsCurrent = undefined;
        });
    }

    /**
     * Resets selection
     */
    reset() {
        if (this.hasSelection) {
            this._selection = [];
            return this.next();
        }
    }

    protected optionToId(option: TOption): TProperty {
        return option as any as TProperty;
    }

    /**
     * Selects option. Will unselect any other options if requested so.
     */
    select(option: TOption, unselectOther: boolean = false): void {
        if (unselectOther) {
            this._selection = [this.optionToId(option)];
        } else {
            if (!this.isSelected(option)) {
                if (!this._selection) {
                    this._selection = [];
                }
                this._selection.push(this.optionToId(option));
            }
        }
        this.next();
    }

    /**
     * Unselects option.
     */
    unselect(option: number | TOption): void {
        const value = typeof option === 'number' ? option : this.optionToId(option);
        this._selection = this._selection?.filter(selectedOptionId => selectedOptionId !== value);
        this.next();
    }

    /**
     * Toggles option. Will unselect any other options if requested so (and selected).
     */
    toggle(option: TOption, unselectOther: boolean = false): void {
        if (this.isSelected(option)) {
            this.unselect(option);
        } else {
            this.select(option, unselectOther);
        }
    }

    /**
     * Checks if option is currently selected
     */
    isSelected(option: TOption): boolean {
        return !!this._selection?.includes(this.optionToId(option));
    }

    /**
     * Applies filter to models collection
     */
    applyFilter(collection: TModel[], selectionOverride?: TProperty[]): TModel[] {
        if (!this.hasSelection && !selectionOverride) {
            return collection;
        }
        if (!selectionOverride) {
            selectionOverride = this.selectedValues;
        }

        const index = this.getIndex();

        const undefinedMatches = this.listing.config.undefinedMatches;

        return collection.filter(
            model =>
                (undefinedMatches && model.data && model.data[this.id] === undefined) ||
                findIndex(selectionOverride, selectionValue => index[selectionValue] && index[selectionValue][model.id]) !== -1
        );
    }

    /**
     * Gets all selected values (when using groups _selection does not contain everything)
     * @return {TProperty[]}
     */
    get selectedValues(): TProperty[] {
        return this.getSelectedValues();
    }

    protected getSelectedValues(): TProperty[] {
        return this._selection || [];
    }

    /**
     * Gets index related to attribute from `Resource`
     */
    getIndex() {
        return this.app.resources[this.listing.type].getIndex(this.id);
    }

    /**
     * Calculates matches for give models collection for all options
     * @param {TModel[]} models  Models array to be checked
     */
    countMatches(models: TModel[]): IMatchesCounts {
        const index = this.getIndex();
        const matches: IMatchesCounts = {};

        this.options.forEach(option => {
            const selectionValue = this.optionToId(option);
            matches[selectionValue] = models.filter(model => index[selectionValue] && index[selectionValue]?.[model.id]).length;
        });
        return matches;
    }

    /**
     * Getter for `_filteredModels`
     * @return {TModel[]}
     */
    get filteredModels(): TModel[] {
        if (!this._filteredModels) {
            this._filteredModels = this.listing.getFilteredModelsExceptFilter([{ filter: this, selection: false }]);
        }
        return this._filteredModels;
    }

    /**
     * _matchesCounts getter
     * It makes sure variable is initatioed
     *
     * @type {any}
     */
    get matchesCounts(): IMatchesCounts {
        if (!this._matchesCounts) {
            this._matchesCounts = this.countMatches(this.listing.getSourceModels());
        }
        return this._matchesCounts;
    }

    /**
     * Counts matches from given source
     * (generalized to workwith both getCurrentMatchesCount and getMatchesCount)
     */
    protected _getCount(source: { [key: string]: number }, option: TOption): number {
        return (option && source[this.optionToId(option)]) || 0;
    }

    /**
     * _matchesCountsCurrent getter
     * It makes sure variable is initatioed
     *
     * @type {IMatchesCounts}
     */
    get matchesCountsCurrent(): IMatchesCounts {
        if (!this._matchesCountsCurrent) {
            const matches: IMatchesCounts = {};
            this.options.forEach(option => {
                const selectionValue = this.optionToId(option);
                matches[selectionValue] = this.listing.getFilteredModelsExceptFilter([
                    { filter: this, selection: [selectionValue] }
                ]).length;
            });
            this._matchesCountsCurrent = matches;
        }
        return this._matchesCountsCurrent;
    }

    /**
     * Gets number of models that match requested option
     * Number is related to filteredModels (except this filter)
     * @param  {AttributeOption} option  Options to check
     * @return {number}
     */
    getMatchesCount(option: TOption): number {
        return this._getCount(this.matchesCounts, option);
    }

    /**
     * If option is availble check
     */
    isUnavailble(option: TOption): boolean {
        return !this.isSelected(option) && !this.getCurrentMatchesCount(option);
    }

    /**
     * Gets number of models that match requested option
     * Number is related to all models
     * @param  { TOption } option  Options to check
     * @return { number }
     */
    getCurrentMatchesCount(option: TOption): number {
        return this._getCount(this.matchesCountsCurrent, option);
    }

    /**
     * Getter for _anyMatches
     * @return {boolean}   true if any matches
     */
    get hasAnyMatches(): boolean {
        if (this._anyMatches === undefined) {
            this._anyMatches = findIndex(Object.values(this.matchesCounts), i => !!i) > -1 ? true : false;
        }
        return this._anyMatches;
    }

    /**
     * Getter for _anyMatches
     * @return {boolean}   true if any matches
     */
    get hasManyMatches(): boolean {
        if (this._manyMatches === undefined) {
            this._manyMatches = Object.values(this.matchesCounts).filter(i => !!i).length > 1 ? true : false;
        }
        return this._manyMatches;
    }

    /**
     * Checks if any model matches any option
     * All models are checked
     * @return {boolean}   true if any matches
     */
    get hasAnyCurrentMatches(): boolean {
        if (this._anyCurrentMatches === undefined) {
            this._anyCurrentMatches = findIndex(Object.values(this.matchesCountsCurrent), i => !!i) > -1 ? true : false;
        }
        return this._anyCurrentMatches;
    }
    /**
     * Checks if any model matches any option
     * All models are checked
     * @return {boolean}   true if any matches
     */
    override hasManyCurrentMatches(): boolean {
        if (this._manyCurrentMatches === undefined) {
            this._manyCurrentMatches = Object.values(this.matchesCountsCurrent).filter(i => !!i).length > 1 ? true : false;
        }
        return this._manyCurrentMatches;
    }

    protected toOptionWithCount(option: TOption, count: number): TOptionWithCount {
        return {
            option,
            count
        } as any as TOptionWithCount;
    }

    get optionsWithMatches(): TOptionWithCount[] {
        if (this._optionsWithMatches === undefined) {
            this._optionsWithMatches = compact(
                this.options.map((option: TOption) => {
                    const count = this.getMatchesCount(option);

                    if (count > 0) {
                        return this.toOptionWithCount(option, count);
                    }

                    return undefined;
                })
            );
        }

        return this._optionsWithMatches;
    }

    get optionsWithCurrentMatches(): TOptionWithCount[] {
        if (this._optionsWithCurrentMatches === undefined) {
            this._optionsWithCurrentMatches = compact(
                this.options.map((option: TOption) => {
                    const count = this.getCurrentMatchesCount(option);

                    if (this.isSelected(option) || count > 0) {
                        return this.toOptionWithCount(option, count);
                    }

                    return undefined;
                })
            );
        }

        return this._optionsWithCurrentMatches;
    }

    protected getOptions(): TOption[] {
        return Object.keys(this.getIndex()) as any as TOption[];
    }

    get options(): TOption[] {
        return this.getOptions();
    }

    /**
     * Selection get / set
     */
    override get selection(): TProperty[] | undefined {
        return this._selection?.length ? this._selection : undefined;
    }

    override set selection(selection: TProperty[] | undefined) {
        this._selection = selection;
    }

    /**
     * Checks if filter is applied
     * @return {boolean}
     */
    get hasSelection(): boolean {
        return !!this._selection?.length;
    }

    getState(): (string | number)[] {
        return compact(this._selection);
    }

    setState(state: (string | number)[]) {
        if (!state) {
            return;
        }
        if (!Array.isArray(state)) {
            state = [state];
        }
        this._selection = state as TProperty[];
    }
}
